import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Users, Heart, PauseCircle } from "lucide-react";
import { InstallButton } from "@/components/InstallButton";
import { PageLoader, Empty } from "@/components/kit";
import { api, errMsg, fmtDate, initials, onColor } from "@/lib/api";
import { useTeamManifest } from "@/lib/pwa";
import { Button } from "@/components/ui/button";
import { hubPages, BottomNav, PageTitle, VsCard, LinkCard, TravelCard, TravelPage } from "./hub/HubPages";
import { Countdown, ResultBadge, HubPost, SponsorWall, FollowForm } from "./hub/HubParts";
import { EmbedCard } from "@/components/EmbedFrame";
import { ShopPage } from "./hub/HubShop";
import { themeClass, themeStyle, themeBg } from "@/lib/theme";
import { useSeo } from "@/lib/seo";

const HubHeader = ({ team, color }) => (
  <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B0F17]/80 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-8">
      <div className="flex min-w-0 items-center gap-3">
        {team.logo_url ? <img src={team.logo_url} alt={team.name} className="h-9 w-9 rounded object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded font-display font-black" style={{ background: color, color: onColor(color) }}>{initials(team.name)}</span>}
        <span className="truncate font-display text-xl font-black uppercase tracking-tight text-white" data-testid="hub-team-name">{team.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <InstallButton label="Install" color="var(--team-ink)" testid="hub-install-btn" className="whitespace-nowrap" />
        <Link to={`/t/${team.slug}/connect`} className="whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider" style={{ background: color, color: onColor(color) }} data-testid="hub-follow-cta">Follow</Link>
      </div>
    </div>
  </header>
);

const Hero = ({ data, color }) => {
  const { team, upcoming, record, fans } = data;
  const next = upcoming.find((e) => e.type === "match") || upcoming[0];
  return (
    <section className="relative overflow-hidden grain">
      <img src={team.hero_image_url || (team.sport === "Football" ? "/img/action.jpg" : "/img/stadium-lights.jpg")} alt={`${team.name} hero`} className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--hub-bg), color-mix(in srgb, var(--hub-bg) 70%, transparent), color-mix(in srgb, var(--hub-bg) 30%, transparent))" }} />
      <div className="absolute -right-24 top-0 hidden h-full w-72 skew-x-[-16deg] opacity-90 lg:block" style={{ background: color }} />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-16 sm:px-8 lg:grid-cols-[1.3fr_1fr] lg:pt-24">
        <div className="rise">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "var(--team-ink)" }}>{team.sport} · Official fan hub</p>
          <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.88] tracking-tight text-white sm:text-6xl lg:text-7xl">{team.name}</h1>
          {team.tagline && <p className="mt-4 max-w-lg text-lg text-slate-300">{team.tagline}</p>}
          <div className="mt-6 flex flex-wrap gap-5 text-sm text-slate-300">
            {team.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{team.location}</span>}
            <span className="flex items-center gap-1.5" data-testid="hub-fan-count"><Heart className="h-4 w-4" />{fans} fans</span>
            <span className="font-mono" data-testid="hub-record">W{record.w} D{record.d} L{record.l}</span>
          </div>
        </div>
        {next && (
          <div className="rise rounded-lg border border-white/10 bg-[#0B0F17]/70 p-6 backdrop-blur-xl" style={{ animationDelay: "120ms" }} data-testid="hub-next-event">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Next up · {next.type}</p>
            <p className="mt-2 font-display text-3xl font-extrabold uppercase text-white">{next.title}</p>
            <p className="mb-5 mt-1 text-sm text-slate-400">{fmtDate(next.starts_at)}{next.venue && ` · ${next.venue}`}</p>
            <Countdown iso={next.starts_at} />
          </div>
        )}
      </div>
    </section>
  );
};

const FixturesSection = ({ upcoming, results, color }) => (
  <section id="fixtures" className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-8 lg:grid-cols-2">
    <div>
      <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">Fixtures</h2>
      <div className="mt-6 space-y-3">
        {upcoming.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-[#111827] p-4" data-testid={`hub-fixture-${e.id}`}>
            <div className="min-w-0"><p className="truncate font-semibold text-white">{e.title}</p><p className="text-xs text-slate-400">{e.venue}{e.competition && ` · ${e.competition}`}</p></div>
            <div className="shrink-0 text-right"><p className="font-mono text-xs" style={{ color: "var(--team-ink)" }}>{fmtDate(e.starts_at)}</p>{e.type === "match" && <p className="text-[10px] uppercase tracking-wider text-slate-500">{e.home_away}</p>}</div>
          </div>
        ))}
        {!upcoming.length && <p className="text-slate-500">No upcoming fixtures announced.</p>}
      </div>
    </div>
    <div>
      <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">Results</h2>
      <div className="mt-6 space-y-3">
        {results.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-[#111827] p-4" data-testid={`hub-result-${e.id}`}>
            <div className="flex min-w-0 items-center gap-3"><ResultBadge e={e} /><div className="min-w-0"><p className="truncate font-semibold text-white">{e.title}</p><p className="text-xs text-slate-500">{fmtDate(e.starts_at)}</p></div></div>
            <p className="shrink-0 font-display text-3xl font-black text-white">{e.our_score}–{e.their_score}</p>
          </div>
        ))}
        {!results.length && <p className="text-slate-500">No results yet.</p>}
      </div>
    </div>
  </section>
);

const SquadSection = ({ squad, color }) => (
  <section id="squad" className="border-y border-white/5 pitch-lines">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
      <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">The squad</h2>
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {squad.map((p) => (
          <div key={p.id} className="group relative overflow-hidden rounded-lg border border-white/[0.08] bg-[#111827] p-5 transition-all duration-300 hover:-translate-y-1" data-testid={`hub-player-${p.id}`}>
            <span className="absolute -right-2 -top-4 font-display text-7xl font-black opacity-20 transition-opacity group-hover:opacity-40" style={{ color: "var(--team-ink)" }}>{p.number ?? ""}</span>
            <p className="relative mt-8 font-display text-xl font-bold uppercase leading-tight text-white">{p.name}</p>
            <p className="relative text-xs text-slate-400">{p.position}</p>
          </div>
        ))}
      </div>
      {!squad.length && <p className="mt-6 text-slate-500">Squad list coming soon.</p>}
    </div>
  </section>
);

export default function TeamHub() {
  const { slug, page } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/public/teams/${slug}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))), [slug]);
  useEffect(() => {
    load();
  }, [load]);
  const color = data?.team?.primary_color || "#CCFF00";
  useTeamManifest(data ? slug : null, "fan", themeBg(data?.team?.theme), data ? `${data.team.name} Fans` : null, (data?.team?.logo_url || "").slice(-12));
  useSeo({ title: data?.team ? `${data.team.name} Fans` : undefined, description: data?.team?.tagline || data?.team?.about, image: data?.team?.hero_image_url || undefined });

  if (error) return <div className="mx-auto max-w-3xl px-4 py-24"><Empty title="Hub not found" text={error}><Link to="/" className="text-[#CCFF00]">Go home</Link></Empty></div>;
  if (!data) return <PageLoader />;
  if (data.paused) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24" data-testid="hub-paused">
        <Empty icon={PauseCircle} title={`${data.team.name} is taking a break`} text="This team hub is temporarily unavailable. Please check back soon." />
      </div>
    );
  }
  const { team } = data;
  const pages = hubPages(data);
  const nonMatch = data.upcoming.filter((e) => e.type !== "match");
  const nextMatch = data.upcoming.find((e) => e.type === "match");
  const wrap = (node) => <main className="mx-auto max-w-7xl px-4 py-12 sm:px-8">{node}</main>;
  const content = {
    "": (
      <>
        <Hero data={data} color={color} />
        <main className="mx-auto max-w-7xl space-y-12 px-4 py-12 sm:px-8">
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent font-bold uppercase" data-testid="home-news-btn"><Link to={`/t/${slug}/news`}>Latest news →</Link></Button>
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent font-bold uppercase" data-testid="home-fixtures-btn"><Link to={`/t/${slug}/fixtures`}>Fixtures & results →</Link></Button>
          </div>
          {data.travel && <TravelCard travel={data.travel} slug={slug} color={color} />}
          {team.about && <p className="text-lg leading-relaxed text-slate-300">{team.about}</p>}
          {data.posts.length > 0 && (
            <section>
              <div className="flex items-end justify-between"><h2 className="font-display text-3xl font-extrabold uppercase text-white">Latest news</h2><Link to={`/t/${slug}/news`} className="text-sm font-bold" style={{ color: "var(--team-ink)" }}>View all →</Link></div>
              <div className="no-scrollbar -mx-4 mt-5 flex snap-x gap-4 overflow-x-auto px-4 pb-2" data-testid="home-news-carousel">{data.posts.slice(0, 6).map((p) => <div key={p.id} className="w-[85%] shrink-0 snap-start sm:w-[45%] lg:w-[32%]"><HubPost p={p} slug={slug} color={color} /></div>)}</div>
            </section>
          )}
          <section>
            <h2 className="mb-5 font-display text-3xl font-extrabold uppercase text-white">Coming up at the club</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {nextMatch ? <VsCard team={team} e={nextMatch} color={color} slug={slug} /> : <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-6 text-slate-400">No fixtures announced yet.</div>}
              <div className="grid gap-4">
                <LinkCard over="Membership" title="Join the club" text={`Become part of the ${team.name} family.`} to={`/t/${slug}/connect`} cta="Join us" testid="home-join-card" />
                <LinkCard over="Club events" title="What's on" text="Matchdays, training, socials and fundraisers." to={`/t/${slug}/events`} cta="View events" testid="home-events-card" />
              </div>
            </div>
          </section>
          {data.shop && <LinkCard over="Club shop" title="Get the kit" text="Official shirts, training wear and club merchandise." to={`/t/${slug}/shop`} cta="Shop now" testid="home-shop-card" />}
          <LinkCard over="Stay connected" title="Never miss a club update" text="Match reminders, results and news straight to your inbox — install the fan app for one-tap access." to={`/t/${slug}/connect`} cta="Follow the club" testid="home-connect-card" />
        </main>
      </>
    ),
    fixtures: <FixturesSection upcoming={data.upcoming.filter((e) => e.type === "match")} results={data.results} color={color} />,
    squad: <SquadSection squad={data.squad} color={color} />,
    news: wrap(<><PageTitle>News & fan zone</PageTitle><div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{data.posts.map((p) => <HubPost key={p.id} p={p} slug={slug} color={color} />)}</div>{!data.posts.length && <p className="text-slate-500">No news yet.</p>}</>),
    events: wrap(<><PageTitle sub="Training, socials and club events">What's on</PageTitle><div className="space-y-3">{nonMatch.map((e) => <div key={e.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-[#111827] p-4" data-testid={`hub-event-${e.id}`}><div><p className="font-semibold text-white">{e.title}</p><p className="text-xs text-slate-400">{e.venue}</p></div><p className="font-mono text-xs" style={{ color: "var(--team-ink)" }}>{fmtDate(e.starts_at)}</p></div>)}{!nonMatch.length && <p className="text-slate-500">No events announced.</p>}</div></>),
    travel: data.travel && wrap(<TravelPage travel={data.travel} slug={slug} color={color} />),
    shop: data.shop && wrap(<ShopPage slug={slug} color={color} />),
    sponsors: data.features?.sponsors && <SponsorWall sponsors={data.sponsors} slug={slug} color={color} />,
    forms: wrap(<><PageTitle>Forms & sign-ups</PageTitle><div className="space-y-6" data-testid="hub-forms-section">{data.embeds.map((e) => <EmbedCard key={e.id} embed={e} />)}</div></>),
    connect: wrap(
      <div style={{ background: `linear-gradient(120deg, ${color}22, transparent 60%)` }} className="rounded-lg p-6 sm:p-10">
        <PageTitle sub="Match news, results and polls straight to your inbox. Install the fan app for one-tap access.">Join the fan club</PageTitle>
        <FollowForm slug={slug} color={color} onFollowed={load} />
        <Link to={`/t/${slug}/players`} className="mt-8 flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="hub-player-app-link"><Users className="h-4 w-4" /> Squad member? Open the player app</Link>
      </div>
    ),
  }[page || ""];
  return (
    <div className={`min-h-screen bg-[#0B0F17] pb-36 ${themeClass(team.theme)}`} style={themeStyle(team.theme, color)} data-testid="team-hub">
      <HubHeader team={team} color={color} />
      {content || wrap(<Empty title="Page not found" text="This section isn't available." />)}
      <footer className="mx-auto max-w-6xl px-4 py-6 text-center text-xs text-slate-500 sm:px-8"><Link to="/" className="hover:text-white" data-testid="hub-powered-by">Powered by SportsDiary</Link></footer>
      <BottomNav slug={slug} pages={pages} color={color} />
    </div>
  );
}
